import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";

export default function EditReminder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { logUserActivity, loading : userLoading } = useActivityLogger();
  
  const [category, setCategory] = useState("Minum obat");
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");
  const [reminder, setReminder] = useState(null);

  // Medicine states
  const [medicineName, setMedicineName] = useState("");
  const [medicineFrequency, setMedicineFrequency] = useState("1x sehari");
  const [medicineUnit, setMedicineUnit] = useState("tablet");
  const [medicineRule, setMedicineRule] = useState("sesudah makan");
  const [medicineTimes, setMedicineTimes] = useState([
    { time: "", quantity: 1 },
  ]);

  // Therapy states - UPDATED
  const [therapyType, setTherapyType] = useState("");
  const [therapyPlace, setTherapyPlace] = useState("");
  const [therapyFrequency, setTherapyFrequency] = useState("1x seminggu");
  const [therapySchedules, setTherapySchedules] = useState([
    { day: "Senin", time: "" },
  ]);

  // Control states
  const [doctorName, setDoctorName] = useState("");
  const [doctorSpecialization, setDoctorSpecialization] = useState("");
  const [facilityType, setFacilityType] = useState("");
  const [facilityName, setFacilityName] = useState("");
  const [controlDate, setControlDate] = useState("");
  const [controlFrequency, setControlFrequency] = useState("1x sebulan");

  // Meal states
  const [mealFrequency, setMealFrequency] = useState("1x sehari");
  const [mealSchedules, setMealSchedules] = useState([
    {
      time: "",
      karbohidrat: "",
      protein: "",
      lemak: "",
      vitamin: "",
      serat: "",
    },
  ]);

  // Fetch reminder data on component mount
  useEffect(() => {
    const fetchReminder = async () => {
      try {
        setFetchLoading(true);
        const response = await api.get(`/pengingat/${id}`);
        const reminderData = response.data;
        setReminder(reminderData);
        
        // Set category based on reminder type
        const categoryMap = {
          obat: "Minum obat",
          terapi: "Terapi",
          kontrol: "Kontrol ke dokter",
          makan: "Makan"
        };
        setCategory(categoryMap[reminderData.tipe] || "Minum obat");
        
        // Populate form fields based on reminder type
        populateFormFields(reminderData);
        if (!userLoading) {
          logUserActivity("reminder", "edit_reminder_view", "EditReminder", `Viewing edit for reminder ID: ${id}`);
        }
      } catch (err) {
        console.error("Failed to fetch reminder:", err);
        setError("Gagal memuat data pengingat");
        if (!userLoading) {
          logUserActivity("reminder", "edit_reminder_fetch_error", "EditReminder", `Failed to fetch reminder ID: ${id}`);
        }
      } finally {
        setFetchLoading(false);
      }
    };
  
    if (id) {
      fetchReminder();
    }
  }, [id, userLoading]);

  const populateFormFields = (reminderData) => {
    const { tipe, details, waktu } = reminderData;
    
    switch (tipe) {
      case "obat":
        if (details) {
          setMedicineName(details.nama_obat || "");
          setMedicineFrequency(details.frekuensi || "1x sehari");
          setMedicineRule(details.aturan || "sesudah makan");
          console.log(reminderData);
          if (details.jadwal && details.jadwal.length > 0) {
            setMedicineTimes(details.jadwal.map(j => ({
              time: j.jam_original || "",
              quantity: j.jumlah || 1
            })));
            setMedicineUnit(details.jadwal[0].satuan || "tablet");
          }
        }
        break;
        
      case "terapi":
        if (details) {
          setTherapyType(details.jenis_terapi || "");
          setTherapyPlace(details.tempat_terapi || "");
          setTherapyFrequency(details.frekuensi || "1x seminggu");
          
          // UPDATED: Handle multiple therapy schedules
          if (details.jadwal && details.jadwal.length > 0) {
            setTherapySchedules(details.jadwal.map(j => ({
              day: j.hari || "Senin",
              time: j.jam || ""
            })));
          }
        }
        break;
        
      case "kontrol":
        if (details) {
          setDoctorName(details.nama_dokter || "");
          setDoctorSpecialization(details.spesialisasi || "");
          setFacilityType(details.jenis_faskes || "");
          setFacilityName(details.nama_faskes || "");
          setControlFrequency(details.frekuensi_kontrol || "1x sebulan");
          
          if (details.jadwal_kontrol?.tanggal_waktu) {
            const date = new Date(details.jadwal_kontrol.tanggal_waktu);
            setControlDate(date.toISOString().slice(0, 16));
          } else if (waktu) {
            const date = new Date(waktu);
            setControlDate(date.toISOString().slice(0, 16));
          }
        }
        break;
        
      case "makan":
        if (details) {
          setMealFrequency(details.frekuensi || "1x sehari");
          
          if (details.jadwal && details.jadwal.length > 0) {
            setMealSchedules(details.jadwal.map(j => ({
              time: j.jam || "",
              karbohidrat: j.karbohidrat || "",
              protein: j.protein || "",
              lemak: j.lemak || "",
              vitamin: j.vitamin || "",
              serat: j.serat || "",
            })));
          }
        }
        break;
        
      default:
        break;
    }
  };

  const handleMedicineFrequencyChange = (value) => {
    setMedicineFrequency(value);
    const count = parseInt(value[0]);
    const newTimes = Array.from(
      { length: count },
      (_, i) => medicineTimes[i] || { time: "", quantity: 1 },
    );
    setMedicineTimes(newTimes);
  };

  // ADDED: Handle therapy frequency change
  const handleTherapyFrequencyChange = (value) => {
    setTherapyFrequency(value);
    const count = parseInt(value[0]);
    const days = [
      "Senin",
      "Selasa",
      "Rabu",
      "Kamis",
      "Jumat",
      "Sabtu",
      "Minggu",
    ];
    const newSchedules = Array.from(
      { length: count },
      (_, i) => therapySchedules[i] || { day: days[i] || "Senin", time: "" }
    );
    setTherapySchedules(newSchedules);
  };

  // ADDED: Handle therapy schedule change
  const handleTherapyScheduleChange = (index, field, value) => {
    const updated = [...therapySchedules];
    updated[index][field] = value;
    setTherapySchedules(updated);
  };

  const handleMealFrequencyChange = (value) => {
    setMealFrequency(value);
    const count = parseInt(value[0]);
    const newSchedules = Array.from(
      { length: count },
      (_, i) =>
        mealSchedules[i] || {
          time: "",
          karbohidrat: "",
          protein: "",
          lemak: "",
          vitamin: "",
          serat: "",
        },
    );
    setMealSchedules(newSchedules);
  };

  const handleTimeChange = (index, field, value) => {
    const updated = [...medicineTimes];
    updated[index][field] = value;
    setMedicineTimes(updated);
  };

  const handleMealScheduleChange = (index, field, value) => {
    const updated = [...mealSchedules];
    updated[index][field] = value;
    setMealSchedules(updated);
  };

  const validateForm = () => {
    setError("");

    switch (category) {
      case "Minum obat":
        if (!medicineName.trim()) {
          setError("Nama obat harus diisi");
          return false;
        }
        for (let i = 0; i < medicineTimes.length; i++) {
          if (!medicineTimes[i].time || !medicineTimes[i].quantity) {
            setError(`Jam dan jumlah obat ke-${i + 1} harus diisi`);
            return false;
          }
          const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
          if (!timeRegex.test(medicineTimes[i].time)) {
            setError(`Format jam obat ke-${i + 1} tidak valid`);
            return false;
          }
        }
        break;

      case "Terapi":
        // UPDATED: Validate therapy schedules
        if (!therapyType.trim() || !therapyPlace.trim()) {
          setError("Jenis terapi dan tempat terapi harus diisi");
          return false;
        }
        for (let i = 0; i < therapySchedules.length; i++) {
          if (!therapySchedules[i].time || !therapySchedules[i].day) {
            setError(`Hari dan jam terapi ke-${i + 1} harus diisi`);
            return false;
          }
          const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
          if (!timeRegex.test(therapySchedules[i].time)) {
            setError(`Format jam terapi ke-${i + 1} tidak valid`);
            return false;
          }
        }
        break;
        
      case "Kontrol ke dokter":
        if (!doctorName.trim() || !facilityName.trim() || !controlDate) {
          setError("Nama dokter, fasilitas kesehatan, dan tanggal kontrol harus diisi");
          return false;
        }
        const controlDateTime = new Date(controlDate);
        if (isNaN(controlDateTime.getTime())) {
          setError("Format tanggal kontrol tidak valid");
          return false;
        }
        break;

      case "Makan":
        if (!mealSchedules[0]?.time) {
          setError("Jam makan pertama harus diisi");
          return false;
        }
        const mealTimeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!mealTimeRegex.test(mealSchedules[0].time)) {
          setError("Format jam makan tidak valid");
          return false;
        }
        break;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      let updateData;

      switch (category) {
        case "Minum obat":
          updateData = {
            judul: `Minum ${medicineName}`,
            tipe: "obat",
            details: {
              nama_obat: medicineName,
              frekuensi: medicineFrequency,
              jadwal: medicineTimes.map((time) => ({
                jam: time.time,
                jumlah: parseInt(time.quantity),
                satuan: medicineUnit,
              })),
              aturan: medicineRule,
            },
          };
          break;

        case "Terapi":
          // UPDATED: Use therapy schedules instead of single time
          const firstSchedule = therapySchedules[0];
          const today = new Date();
          const [hours, minutes] = firstSchedule.time.split(":");
          const therapyDateTime = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            parseInt(hours),
            parseInt(minutes),
          );

          updateData = {
            tipe: "terapi",
            judul: `${therapyType} di ${therapyPlace}`,
            waktu: therapyDateTime.toISOString(),
            details: {
              jenis_terapi: therapyType,
              tempat_terapi: therapyPlace,
              frekuensi: therapyFrequency,
              jadwal: therapySchedules.map((schedule) => ({
                hari: schedule.day,
                jam: schedule.time,
              })),
            },
          };
          break;

        case "Kontrol ke dokter":
          const controlDateTime = new Date(controlDate);
          updateData = {
            tipe: "kontrol",
            judul: `Kontrol ke ${doctorName}`,
            waktu: controlDateTime.toISOString(),
            details: {
              nama_dokter: doctorName,
              spesialisasi: doctorSpecialization,
              jenis_faskes: facilityType,
              nama_faskes: facilityName,
              jadwal_kontrol: {
                tanggal_waktu: controlDateTime.toISOString(),
              },
              frekuensi_kontrol: controlFrequency,
              jadwal_pengingat: [{ offset: "1h" }, { offset: "1d" }],
            },
          };
          break;

        case "Makan":
          const todayMeal = new Date();
          const [mealHours, mealMinutes] = mealSchedules[0].time.split(":");
          const mealDateTime = new Date(
            todayMeal.getFullYear(),
            todayMeal.getMonth(),
            todayMeal.getDate(),
            parseInt(mealHours),
            parseInt(mealMinutes),
          );

          updateData = {
            tipe: "makan",
            judul: `Jadwal Makan (${mealFrequency})`,
            waktu: mealDateTime.toISOString(),
            details: {
              frekuensi: mealFrequency,
              jadwal: mealSchedules.map((schedule) => ({
                jam: schedule.time,
                sudah_makan: false,
                karbohidrat: schedule.karbohidrat || "",
                protein: schedule.protein || "",
                lemak: schedule.lemak || "",
                vitamin: schedule.vitamin || "",
                serat: schedule.serat || "",
              })),
            },
          };
          break;

        default:
          setError("Kategori pengingat tidak valid");
          setLoading(false);
          return;
      }

      // Update the reminder
      await api.put(`/pengingat/${id}`, updateData);
      
      logUserActivity("reminder", "edit_reminder_success", "EditReminder", `Successfully updated reminder ID: ${id}`);
      
      // Navigate back to reminders page
      navigate("/reminders");
    } catch (err) {
      console.error("Failed to update reminder:", err);
      setError(err.response?.data?.error || "Gagal memperbarui pengingat");
      logUserActivity("reminder", "edit_reminder_error", "EditReminder", `Failed to update reminder ID: ${id}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Apakah Anda yakin ingin menghapus pengingat ini?")) {
      try {
        setLoading(true);
        await api.delete(`/pengingat/${id}`);
        logUserActivity("reminder", "delete_reminder_from_edit", "EditReminder", `Deleted reminder ID: ${id}`);
        navigate("/reminders");
      } catch (err) {
        console.error("Failed to delete reminder:", err);
        setError("Gagal menghapus pengingat");
        logUserActivity("reminder", "delete_reminder_error", "EditReminder", `Failed to delete reminder ID: ${id}: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  if (fetchLoading) {
    return (
      <div className="min-h-screen bg-white px-4 py-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-gray-600">Memuat data pengingat...</p>
        </div>
      </div>
    );
  }

  if (!reminder) {
    return (
      <div className="min-h-screen bg-white px-4 py-4">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/reminders")}>
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">Edit Pengingat</h1>
          <div className="h-6 w-6" />
        </div>
        <div className="text-center mt-20">
          <p className="text-red-600 mb-4">Pengingat tidak ditemukan</p>
          <button
            onClick={() => navigate("/reminders")}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
          >
            Kembali ke Pengingat
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-4 py-4 pb-32 flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate("/reminders")}>
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-semibold">Edit Pengingat</h1>
        <button
          onClick={handleDelete}
          className="text-red-500 hover:text-red-700"
          disabled={loading}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="flex flex-col flex-grow space-y-5"
      >
        <div>
          <label className="text-sm font-medium mb-2 block">
            Kategori Pengingat
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2 bg-white"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={loading}
          >
            <option>Minum obat</option>
            <option>Terapi</option>
            <option>Kontrol ke dokter</option>
            <option>Makan</option>
          </select>
        </div>

        {/* Medicine section - unchanged */}
        {category === "Minum obat" && (
          <>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Nama Obat *
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Tensicap"
                value={medicineName}
                onChange={(e) => setMedicineName(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Frekuensi Minum Obat
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={medicineFrequency}
                onChange={(e) => handleMedicineFrequencyChange(e.target.value)}
                disabled={loading}
              >
                <option>1x sehari</option>
                <option>2x sehari</option>
                <option>3x sehari</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Satuan Obat
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={medicineUnit}
                onChange={(e) => setMedicineUnit(e.target.value)}
                disabled={loading}
              >
                <option>tablet</option>
                <option>kapsul</option>
                <option>sirup</option>
                <option>tetes</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Aturan Minum
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={medicineRule}
                onChange={(e) => setMedicineRule(e.target.value)}
                disabled={loading}
              >
                <option>sesudah makan</option>
                <option>sebelum makan</option>
                <option>bersama makan</option>
              </select>
            </div>
            <div className="space-y-3">
              {medicineTimes.map((entry, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between border px-3 py-2 rounded-lg"
                >
                  <div className="flex items-center space-x-2">
                    <label>Jam *</label>
                    <input
                      type="time"
                      value={entry.time}
                      onChange={(e) =>
                        handleTimeChange(index, "time", e.target.value)
                      }
                      className="border rounded px-2 py-1 text-sm"
                      disabled={loading}
                      required
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="1"
                      value={entry.quantity}
                      onChange={(e) =>
                        handleTimeChange(index, "quantity", e.target.value)
                      }
                      className="w-12 border rounded px-2 py-1 text-sm text-center"
                      disabled={loading}
                      required
                    />
                    <span className="text-sm text-gray-700">
                      {medicineUnit}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* UPDATED: Therapy section */}
        {category === "Terapi" && (
          <>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Jenis Terapi *
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Terapi fisik"
                value={therapyType}
                onChange={(e) => setTherapyType(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Tempat Terapi *
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Klinik"
                value={therapyPlace}
                onChange={(e) => setTherapyPlace(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Frekuensi Terapi
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={therapyFrequency}
                onChange={(e) => handleTherapyFrequencyChange(e.target.value)}
                disabled={loading}
              >
                <option>1x seminggu</option>
                <option>2x seminggu</option>
                <option>3x seminggu</option>
                <option>4x seminggu</option>
                <option>5x seminggu</option>
                <option>6x seminggu</option>
                <option>7x seminggu</option>
              </select>
            </div>

            {/* Jadwal Terapi */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Jadwal Terapi
              </label>
              <div className="space-y-3">
                {therapySchedules.map((schedule, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 border rounded-lg p-3"
                  >
                    <div className="flex-1">
                      <label className="text-xs text-gray-600 mb-1 block">
                        Hari ke-{index + 1}
                      </label>
                      <select
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={schedule.day}
                        onChange={(e) =>
                          handleTherapyScheduleChange(
                            index,
                            "day",
                            e.target.value
                          )
                        }
                        disabled={loading}
                        required
                      >
                        <option value="Senin">Senin</option>
                        <option value="Selasa">Selasa</option>
                        <option value="Rabu">Rabu</option>
                        <option value="Kamis">Kamis</option>
                        <option value="Jumat">Jumat</option>
                        <option value="Sabtu">Sabtu</option>
                        <option value="Minggu">Minggu</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-gray-600 mb-1 block">
                        Jam *
                      </label>
                      <input
                        type="time"
                        value={schedule.time}
                        onChange={(e) =>
                          handleTherapyScheduleChange(
                            index,
                            "time",
                            e.target.value
                          )
                        }
                        className="w-full border rounded px-2 py-1 text-sm"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Control section - unchanged */}
        {category === "Kontrol ke dokter" && (
          <>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Nama Dokter *
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Dr. Shakira"
                value={doctorName}
                onChange={(e) => setDoctorName(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Spesialisasi Dokter
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Neurologist"
                value={doctorSpecialization}
                onChange={(e) => setDoctorSpecialization(e.target.value)}
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Jenis Fasilitas Kesehatan
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={facilityType}
                onChange={(e) => setFacilityType(e.target.value)}
                disabled={loading}
              >
                <option value="" className="">Pilih jenis fasilitas kesehatan</option>
                <option value="Rumah Sakit">Rumah Sakit/RS</option>
                <option value="Klinik">Klinik</option>
                <option value="Puskesmas">Puskesmas</option>
                <option value="Lainnya">Lainnya</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Nama Fasilitas Kesehatan *
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="RS Hermina Manado"
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Jadwal Kontrol Terdekat *
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                type="datetime-local"
                value={controlDate}
                onChange={(e) => setControlDate(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Frekuensi Kontrol
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={controlFrequency}
                onChange={(e) => setControlFrequency(e.target.value)}
                disabled={loading}
              >
                <option>1x sebulan</option>
                <option>1x dalam 3 bulan</option>
                <option>1x dalam 6 bulan</option>
              </select>
            </div>
          </>
        )}

        {/* Meal section - unchanged */}
        {category === "Makan" && (
          <>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Frekuensi Makan
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={mealFrequency}
                onChange={(e) => handleMealFrequencyChange(e.target.value)}
                disabled={loading}
              >
                <option>1x sehari</option>
                <option>2x sehari</option>
                <option>3x sehari</option>
              </select>
            </div>

            {mealSchedules.map((schedule, i) => (
              <div key={i}>
                <hr className="my-3" />
                <label className="text-sm font-medium mb-2 block">
                  Jadwal Makan ke-{i + 1} *
                </label>
                <div className="mb-2">
                  <input
                    className="w-full border rounded-lg px-3 py-2"
                    type="time"
                    value={schedule.time}
                    onChange={(e) =>
                      handleMealScheduleChange(i, "time", e.target.value)
                    }
                    disabled={loading}
                    required
                  />
                </div>
                <input
                  className="w-full border rounded-lg px-3 py-2 mb-1"
                  placeholder="Karbohidrat (opsional)"
                  value={schedule.karbohidrat}
                  onChange={(e) =>
                    handleMealScheduleChange(i, "karbohidrat", e.target.value)
                  }
                  disabled={loading}
                />
                <input
                  className="w-full border rounded-lg px-3 py-2 mb-1"
                  placeholder="Protein (opsional)"
                  value={schedule.protein}
                  onChange={(e) =>
                    handleMealScheduleChange(i, "protein", e.target.value)
                  }
                  disabled={loading}
                />
                <input
                  className="w-full border rounded-lg px-3 py-2 mb-1"
                  placeholder="Lemak (opsional)"
                  value={schedule.lemak}
                  onChange={(e) =>
                    handleMealScheduleChange(i, "lemak", e.target.value)
                  }
                  disabled={loading}
                />
                <input
                  className="w-full border rounded-lg px-3 py-2 mb-1"
                  placeholder="Vitamin & Mineral (opsional)"
                  value={schedule.vitamin}
                  onChange={(e) =>
                    handleMealScheduleChange(i, "vitamin", e.target.value)
                  }
                  disabled={loading}
                />
                <input
                  className="w-full border rounded-lg px-3 py-2 mb-1"
                  placeholder="Serat (opsional)"
                  value={schedule.serat}
                  onChange={(e) =>
                    handleMealScheduleChange(i, "serat", e.target.value)
                  }
                  disabled={loading}
                />
              </div>
            ))}
          </>
        )}
      </form>

      <div className="fixed bottom-0 left-0 w-full bg-white px-4 py-4 border-t flex flex-col space-y-3">
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={loading || fetchLoading}
          className="w-full bg-blue-500 text-white rounded-lg py-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Menyimpan..." : "Simpan Perubahan"}
        </button>
        <button
          type="button"
          onClick={() => navigate("/reminders")}
          disabled={loading}
          className="w-full border border-gray-300 text-gray-700 rounded-lg py-3 font-medium disabled:opacity-50"
        >
          Batal
        </button>
      </div>
    </div>
  );
}